// MA03 순수 변환 로직 — request 비의존 모듈 (GL11 mapping.ts 선례).
// 테스트에서 request 실모듈(okta-auth-js→@peculiar/webcrypto CJS)을 끌고 오면 CI Node 에서 깨진다 —
// 경계에서 끊는 레포 선례 준수. 여기에는 부수효과 없는 매핑만 둔다.
import type { AuthManagementParams } from './useGetAuthManagementList'
import { ITEM_EDIT_CASCADE } from '../consts'
import type { AuthMenuRow } from '../consts'

// 조회영역 값 → 조회 조건 변환은 index.tsx(컨덕터 뷰 키)가 한다. 여기는 URL/세션 왕복만.
export const asList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) return v.length ? v.map(String) : undefined
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

// ── 검색조건 저장/복원 (menuOnSession — ST09 재고현황 searchQuery.ts 준용) ──────────────
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 형태는 AuthManagementParams 그대로 — Utils.qsStringify(comma) ↔ useParsedQuery(comma) 왕복.
// authCodes 는 항목이 하나면 파서가 문자열을 주므로 asList 로 양쪽을 모두 받는다.

const strOrUndef = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/**
 * URL 쿼리(파싱 결과) → 복원 검색조건. 화주(필수 필터)가 없으면 복원 대상이 아니다(undefined).
 * 조작된 URL 이 폼/쿼리를 깨뜨리지 않도록 타입이 맞는 값만 받는다.
 */
export const fromSearchQuery = (query: Record<string, unknown>): AuthManagementParams | undefined => {
  const shipperCode = strOrUndef(query.shipperCode)
  if (!shipperCode) return undefined
  return { shipperCode, authCodes: asList(query.authCodes) }
}

type CascadeRow = AuthMenuRow & { key: string }
/** editCascade(schema n-item-table) — 상위 메뉴 비허용이면 같은 권한의 하위 전부 비허용, 하위 허용이면 상위 복원(옛 onItemCellChange 그대로). changedKeys = 이번에 허용여부가 바뀐 행.
 *  셀 편집은 한 번에 한 행이라 changedKeys 는 보통 1건이다 — 상위·하위가 함께 오면 순서대로 겹쳐 적용된다(일괄 편집이 생기면 규칙을 다시 정한다, 봇 리뷰). */
export const cascadeMenuAct = <T extends CascadeRow>(rows: T[], changedKeys: Set<string>): T[] =>
  rows
    .filter((r) => changedKeys.has(r.key))
    .reduce((out, target) => {
      const isParent = target[ITEM_EDIT_CASCADE.parentFlag.field] === ITEM_EDIT_CASCADE.parentFlag.value
      const act = target[ITEM_EDIT_CASCADE.field]
      if (isParent && act === ITEM_EDIT_CASCADE.downValue) {
        return out.map((r) =>
          r.AUTH_CD === target.AUTH_CD && r[ITEM_EDIT_CASCADE.treeField] === target[ITEM_EDIT_CASCADE.keyField] ? { ...r, [ITEM_EDIT_CASCADE.field]: ITEM_EDIT_CASCADE.downValue } : r,
        )
      }
      if (!isParent && act === ITEM_EDIT_CASCADE.upValue) {
        return out.map((r) =>
          r.AUTH_CD === target.AUTH_CD && r[ITEM_EDIT_CASCADE.parentFlag.field] === ITEM_EDIT_CASCADE.parentFlag.value && r[ITEM_EDIT_CASCADE.keyField] === target[ITEM_EDIT_CASCADE.treeField]
            ? { ...r, [ITEM_EDIT_CASCADE.field]: ITEM_EDIT_CASCADE.upValue }
            : r,
        )
      }
      return out
    }, rows)
